import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Home, Menu, MessageSquare, LogIn, LogOut, User, Crown, Info, HelpCircle, Shield, Trophy, Users, Download, BarChart3, BookOpen, Building2, Library, Smile } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { useLanguage } from '@/context/LanguageContext';
import { LanguageToggle } from '@/components/LanguageToggle';
import InstallPWA from '@/components/InstallPWA';

// Detective Dino icon - aquamarine dino with white eye and magnifying glass
// Values: 0=empty, 1=dino color, 2=white (eye)
const DinoIcon = () => {
  const pixels = [
    [0,0,0,0,0,0,1,1,1,1,1,1],
    [0,0,0,0,0,1,1,1,1,1,1,1,1],
    [0,0,0,0,0,1,1,2,1,1,1,1,1],
    [0,0,0,0,0,1,1,1,1,1,1,1],
    [0,0,0,0,0,1,1,1,1,0,0,0],
    [0,0,0,1,0,1,1,1,1,1,0,0],
    [0,0,1,1,1,1,1,1,1,1,0,0],
    [0,1,1,1,1,1,1,1,1,0,0,0],
    [1,1,1,1,1,1,1,1,0,0,0,0],
    [0,1,1,1,1,1,1,1,0,0,0,0],
    [0,0,1,1,1,1,1,0,0,0,0,0],
    [0,0,0,1,1,0,1,1,0,0,0,0],
    [0,0,0,1,0,0,0,1,0,0,0,0],
    [0,0,0,1,1,0,0,0,0,0,0,0],
  ];
  const fills = { 1: '#00E5CC', 2: '#FFFFFF' };
  
  return (
    <svg width="44" height="34" viewBox="-2 4 64 52" fill="none" xmlns="http://www.w3.org/2000/svg">
      {pixels.map((row, y) => 
        row.map((pixel, x) => 
          pixel > 0 && (
            <rect
              key={`${x}-${y}`}
              x={x * 4}
              y={y * 4}
              width="4"
              height="4"
              fill={fills[pixel]}
            />
          )
        )
      )}
      
      {/* Itty bitty arm holding the magnifying glass */}
      <line x1="40" y1="28" x2="46" y2="32" stroke="#00E5CC" strokeWidth="3" strokeLinecap="round"/>
      
      {/* Magnifying glass - closer to dino, all purple, smaller */}
      <circle cx="52" cy="26" r="5" fill="none" stroke="#7C3AED" strokeWidth="2"/>
      <circle cx="52" cy="26" r="3" fill="#7C3AED" fillOpacity="0.15"/>
      <line x1="48" y1="30" x2="44" y2="34" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round"/>
      
      {/* Purple thought bubble with ? - detective thinking */}
      <circle cx="6" cy="10" r="4.5" fill="#7C3AED"/>
      <circle cx="13" cy="11" r="1.5" fill="#7C3AED"/>
      <circle cx="17" cy="13" r="1" fill="#7C3AED"/>
      <text x="6" y="12.5" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">?</text>
    </svg>
  );
};

export const Header = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { subscriptionStatus } = useSubscription();
  const { t, isSpanish } = useLanguage();

  // "Hi-I'm-here" pulse indicator on the hamburger menu for the first 7
  // days after signup, until the user has seen the first-visit tour. This
  // gently draws the eye to the menu (Stats, For Organizations, FAQs,
  // account) once the tour is dismissed. Lane G enhancement.
  const showMenuPulse = React.useMemo(() => {
    if (!isAuthenticated || !user?.created_at) return false;
    try {
      const ageDays = (Date.now() - new Date(user.created_at).getTime()) / (1000 * 60 * 60 * 24);
      if (ageDays > 7) return false;
      const tourSeen = typeof window !== 'undefined' && localStorage.getItem('cca_tour_seen_v1') === '1';
      const menuDismissed = typeof window !== 'undefined' && localStorage.getItem('cca_menu_pulse_dismissed') === '1';
      return !menuDismissed && (ageDays <= 7) && (!tourSeen || ageDays <= 3);
    } catch {
      return false;
    }
  }, [isAuthenticated, user?.created_at]);
  
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Controlled-open state for the hamburger dropdown. The first-visit
  // tour needs to force the menu open while walking through each menu
  // item — Radix portals menu items asynchronously, so a single
  // .click() call doesn't synchronously put them in the DOM for
  // driver.js to query. By controlling the `open` prop ourselves AND
  // listening for `cca:tour-force-menu-open` / `cca:tour-release-menu`
  // events, we keep the dropdown reliably open during the walkthrough.
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [tourForcedOpen, setTourForcedOpen] = React.useState(false);

  React.useEffect(() => {
    const handleForce = () => { setTourForcedOpen(true); setMenuOpen(true); };
    const handleRelease = () => { setTourForcedOpen(false); setMenuOpen(false); };
    window.addEventListener('cca:tour-force-menu-open', handleForce);
    window.addEventListener('cca:tour-release-menu', handleRelease);
    return () => {
      window.removeEventListener('cca:tour-force-menu-open', handleForce);
      window.removeEventListener('cca:tour-release-menu', handleRelease);
    };
  }, []);
  
  const isPremium = subscriptionStatus?.status === 'active' || 
                    subscriptionStatus?.status === 'trial' || 
                    subscriptionStatus?.status === 'admin' ||
                    user?.is_gifted || 
                    user?.is_admin;
  
  return (
    <header className="sticky top-0 z-[60] w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between px-2 sm:px-4 gap-2">
        <Link to="/" data-testid="header-logo" className="flex items-center space-x-1.5 sm:space-x-2 group flex-shrink-0">
          <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-white border border-gray-200 transition-transform group-hover:scale-105 overflow-hidden flex-shrink-0">
            <DinoIcon />
          </div>
          <div className="flex flex-col">
            <span className="font-semibold text-[11px] sm:text-sm md:text-lg text-[#00A896] dark:text-[#00E5CC] leading-tight whitespace-nowrap">
              ClearCommunicationApp
            </span>
            <span className="text-[8px] sm:text-[10px] md:text-xs text-muted-foreground whitespace-nowrap">{t('decodeCommunication')}</span>
          </div>
        </Link>
        
        <nav className="flex items-center gap-1 sm:gap-2 flex-shrink-0" data-testid="header-nav">
          {/* Install PWA Button - always visible with responsive styling */}
          <div data-testid="header-install-wrap"><InstallPWA /></div>
          
          {/* Language Toggle - in header */}
          <div data-testid="header-language-toggle"><LanguageToggle compact={true} /></div>

          {/* Subscription badge intentionally removed from header per
              May 12 UX feedback — it wasn't clickable and crowded the
              header. VIP status is still visible on the Profile page. */}

          {/* Main Menu — modal=false so the dropdown does NOT lock body
              scroll. Radix's default modal behaviour adds `overflow: hidden`
              + scrollbar-padding to <body>, which breaks `position: sticky`
              on the header AND can intercept taps on mobile Chrome.
              Controlled `open` so the first-visit tour can force it open
              while walking through each menu item (Radix portals items
              asynchronously, so a single .click() puts them in the DOM
              too late for driver.js to query). */}
          <DropdownMenu modal={false} open={menuOpen} onOpenChange={(open) => {
            // Block close attempts while the tour is forcibly holding
            // the menu open. Otherwise Radix dismisses the menu when
            // driver.js's overlay receives a click.
            if (tourForcedOpen && !open) return;
            setMenuOpen(open);
            if (open && showMenuPulse) {
              try { localStorage.setItem('cca_menu_pulse_dismissed', '1'); } catch { /* ignore */ }
            }
          }}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="relative text-muted-foreground hover:text-foreground px-2 sm:px-3 min-h-[44px] min-w-[44px]"
                data-testid="header-menu-btn"
                aria-label={t('menu')}
              >
                <Menu className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('menu')}</span>
                {showMenuPulse && (
                  <span
                    className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5"
                    data-testid="menu-pulse-indicator"
                    aria-hidden="true"
                  >
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00E5CC] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#00E5CC]"></span>
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={8}
              collisionPadding={16}
              className="w-64 z-[80] [&>*]:min-h-[44px] [&>div]:py-2"
            >
              {/* 1. Home — brand purple tint to anchor the menu's top.
                  Matches the For Organizations gray-tint at the bottom
                  so the two emphasis colors bracket the regular items. */}
              <DropdownMenuItem
                onClick={() => navigate('/')}
                className="cursor-pointer bg-[#8B5CF6]/10 hover:bg-[#8B5CF6]/20 focus:bg-[#8B5CF6]/20 text-[#7C3AED] dark:text-[#A78BFA] font-medium"
                data-testid="menu-home"
              >
                <Home className="h-4 w-4 mr-2" />
                {t('home')}
              </DropdownMenuItem>
              {/* 2. Blog */}
              <DropdownMenuItem onClick={() => navigate('/blog')} className="cursor-pointer" data-testid="menu-blog">
                <BookOpen className="h-4 w-4 mr-2" />
                Blog
              </DropdownMenuItem>
              {/* 3. Communication Emojis — sits right under Blog per
                  user-requested ordering. Public-browse, gated download. */}
              <DropdownMenuItem onClick={() => navigate('/communication-emojis')} className="cursor-pointer" data-testid="menu-communication-emojis">
                <Smile className="h-4 w-4 mr-2" />
                {isSpanish ? 'Emojis de Comunicación' : 'Communication Emojis'}
              </DropdownMenuItem>
              {/* 4. Resources — moved BEFORE Stats per UX feedback. The
                  page itself gates content behind login. */}
              <DropdownMenuItem onClick={() => navigate('/resources')} className="cursor-pointer" data-testid="menu-resources">
                <Library className="h-4 w-4 mr-2" />
                {isSpanish ? 'Recursos' : 'Resources'}
              </DropdownMenuItem>
              {/* 4. Stats & Leaderboard — visible to guests too. The
                  /leaderboard route is wrapped in <GuestRestricted>
                  which shows a "Create Free Account" sign-up gate
                  instead of the actual leaderboard, matching the
                  Resources / Friends pattern. */}
              <DropdownMenuItem onClick={() => navigate('/leaderboard')} className="cursor-pointer" data-testid="menu-stats-leaderboard">
                <Trophy className="h-4 w-4 mr-2" />
                {isSpanish ? 'Estadísticas y ranking' : 'Stats & Leaderboard'}
              </DropdownMenuItem>
              {/* 5. For Organizations — slate background BECOMES the
                  section divider (replaces the previous
                  <DropdownMenuSeparator /> + plain item combo that left
                  visible dead space above it). One element does both
                  jobs: visually splits the public nav from the account
                  block AND remains a clickable destination. */}
              <DropdownMenuItem
                onClick={() => navigate('/for-organizations')}
                className="cursor-pointer bg-slate-200/60 dark:bg-slate-700/40 hover:bg-slate-200 dark:hover:bg-slate-700 focus:bg-slate-200 dark:focus:bg-slate-700 font-medium text-slate-800 dark:text-slate-100"
                data-testid="menu-for-organizations"
              >
                <Building2 className="h-4 w-4 mr-2" />
                For Organizations
              </DropdownMenuItem>
              {/* 6. Account section — directly after the slate band so
                  there is no second divider creating dead space. */}
              {isAuthenticated ? (
                <>
                  <div className="px-2 pt-1 pb-0.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                    {isSpanish ? 'Tu cuenta' : 'Your Account'}
                  </div>
                  <div
                    className="px-2 py-1.5 cursor-pointer hover:bg-muted rounded-sm transition-colors"
                    onClick={() => navigate('/profile')}
                    data-testid="menu-account-block"
                  >
                    <div className="flex items-center gap-2">
                      {user?.picture ? (
                        <img src={user.picture} alt="" className="h-6 w-6 rounded-full" />
                      ) : (
                        <User className="h-4 w-4" />
                      )}
                      <div className="flex flex-col">
                        <p className="text-sm font-medium">{user?.name || user?.display_name}</p>
                        <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                      </div>
                    </div>
                  </div>
                  {/* 8. Admin */}
                  {user?.is_admin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer text-[#00A896] dark:text-[#00E5CC]">
                      <Shield className="h-4 w-4 mr-2" />
                      Admin Dashboard
                    </DropdownMenuItem>
                  )}
                </>
              ) : (
                <DropdownMenuItem onClick={() => navigate('/login')} className="cursor-pointer">
                  <LogIn className="h-4 w-4 mr-2" />
                  {t('login')}
                </DropdownMenuItem>
              )}
              {/* About link removed from hamburger per UX feedback —
                  now lives only on the FAQs page and via the clickable
                  @clearcommunicationapp wordmark on the linktree. */}
              {/* FAQs / Feedback — combined per UX feedback. The FAQs page
                  now embeds the feedback/request form so users have one
                  destination for both reading and submitting. */}
              <DropdownMenuItem onClick={() => navigate('/faqs')} className="cursor-pointer">
                <HelpCircle className="h-4 w-4 mr-2" />
                {isSpanish ? 'Preguntas frecuentes / Comentarios' : 'FAQs / Feedback'}
              </DropdownMenuItem>
              {/* 11. Logout */}
              {isAuthenticated && (
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600">
                  <LogOut className="h-4 w-4 mr-2" />
                  {isSpanish ? 'Nos vemos luego, cocodrilo' : 'See you later, alligator'}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>
      </div>
    </header>
  );
};
